import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Phone, Bell } from 'lucide-react';
import { listCalls } from '../services/monitoringApi';
import GradeChip from './GradeChip';
import tokens from '../design/tokens';

/**
 * Calls & alerts — every call we made and every alert we raised about this
 * customer's motors, newest first. Read-only: entries are written by staff
 * (onboarding repo, scripts/log-call.mjs) or, for the sample-data account,
 * derived from the demo series by generate-demo-fleet.mjs --refresh.
 *
 * Same visual system as Fleet and MotorDetail: ivory ground, white cards,
 * Plex Mono for every date and reading. Call/Alert tags are navy, never a
 * status colour — the only status colour here is the grade chip.
 */

const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;
const MONO = { fontFamily: F.mono };
const DISP = { fontFamily: F.display };

/** "Sep 23, 2026 · 10:00 AM" in the viewer's local time. */
export function formatWhen(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

function KindTag({ kind }) {
  const isCall = kind === 'call';
  const Icon = isCall ? Phone : Bell;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.ink, background: C.surfaceAlt, borderRadius: R.full, padding: '3px 10px' }}>
      <Icon size={12} strokeWidth={2} aria-hidden="true" /> {isCall ? 'Call' : 'Alert'}
    </span>
  );
}

function Label({ children }) {
  return <div style={{ ...MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 4 }}>{children}</div>;
}

function Entry({ e, onSelectMotor }) {
  return (
    <li className="bs-call" style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 18, listStyle: 'none' }}>
      <div className="bs-call-when">
        <div style={{ ...MONO, fontSize: 13, color: C.ink }}>{formatWhen(e.at)}</div>
        <div style={{ marginTop: 8 }}><KindTag kind={e.kind} /></div>
      </div>
      <div className="bs-call-motor">
        {onSelectMotor ? (
          <button type="button" onClick={() => onSelectMotor(e.motorId)}
                  style={{ all: 'unset', cursor: 'pointer', ...DISP, fontWeight: 700, fontSize: 16, color: C.ink, textDecoration: 'underline', textDecorationColor: C.hairlineStrong, textUnderlineOffset: 3 }}>
            {e.motorName}
          </button>
        ) : (
          <div style={{ ...DISP, fontWeight: 700, fontSize: 16, color: C.ink }}>{e.motorName}</div>
        )}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <GradeChip grade={e.gradeAtTime} noData="Offline" title={e.gradeAtTime === '-' ? 'Sensor not reporting' : 'Grade at the time'} />
          {e.velocityAtTime != null && <span style={{ ...MONO, fontSize: 12, color: C.textMuted }}>{Number(e.velocityAtTime).toFixed(2)} mm/s</span>}
        </div>
      </div>
      <div className="bs-call-told">
        {/* An alert is what the sensors showed; a call is what we said. */}
        <Label>{e.kind === 'alert' ? 'What we saw' : 'What we told you'}</Label>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: C.text }}>{e.told}</div>
      </div>
      <div className="bs-call-action">
        <Label>Action taken</Label>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: C.text }}>{e.action}</div>
      </div>
    </li>
  );
}

export default function CallsAlerts({ customerId, customerName, onSelectMotor, shopPhone }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await listCalls(customerId);
      setEntries(res.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load calls and alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        .bs-call { display: grid; grid-template-columns: 190px 220px minmax(0,1fr) minmax(0,0.8fr); gap: 20px; align-items: start; }
        @media (max-width: 900px) { .bs-call { grid-template-columns: 170px minmax(0,1fr); } .bs-call-told, .bs-call-action { grid-column: 1 / -1; } }
        @media (max-width: 560px) { .bs-call { grid-template-columns: minmax(0,1fr); gap: 14px; } }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ ...MONO, fontSize: 12, letterSpacing: F.tracking.eyebrow, color: C.link, textTransform: 'uppercase' }}>
            {customerName || 'Your plant'}
          </div>
          <h1 style={{ margin: 0, ...DISP, fontWeight: 700, fontSize: 30, letterSpacing: F.tracking.tight, color: C.ink }}>Calls &amp; alerts</h1>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted }}>Every call we made and every alert we raised about your equipment, newest first.</p>
        </div>
        <button onClick={() => { setRefreshing(true); load(); }} disabled={refreshing}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: R.base, background: C.surface, border: `1px solid ${C.hairline}`, color: C.text, fontSize: 13, fontWeight: 600, cursor: refreshing ? 'default' : 'pointer', fontFamily: F.body, opacity: refreshing ? 0.6 : 1 }}>
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 96, borderRadius: R.xl }} />)}
        </div>
      ) : error ? (
        <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 20, fontSize: 15, color: C.text }}>
          We couldn't load your calls and alerts. Try Refresh{shopPhone ? `, or call us · ${shopPhone}` : ''}.
        </div>
      ) : entries.length === 0 ? (
        <div style={{ background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 20, fontSize: 15, color: C.text }}>
          No calls or alerts yet. If equipment crosses a limit, we call you — typically the same business day — and the call shows up here.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, display: 'grid', gap: 12 }}>
          {entries.map((e) => <Entry key={`${e.at}-${e.motorId}-${e.kind}`} e={e} onSelectMotor={onSelectMotor} />)}
        </ul>
      )}
    </div>
  );
}
